/**
 * Copyright 2013-present, Novivia, Inc.
 * All rights reserved.
 */

import AEAbsenteeBidModule from "@auex/react-components/AbsenteeBidModule";
import AEAcceptedPaymentMethods from "../components/AcceptedPaymentMethods";
import AEBidIncrementsEditor from "@auex/react-components/BidIncrementsEditor";
import AELotPicture from "@auex/react-components/LotPicture";
import AELotStatusLabel from "@auex/react-components/LotStatusLabel";
import AEShareBar from "@auex/react-components/ShareBar";
import AEWatchButton from "@auex/react-components/WatchButton";
import AuctionInformationTabs from "components/AuctionInformationTabs";
import {bindIfLoggedIn} from "authentication";
import BSButton from "react-bootstrap/Button";
import BSButtonToolbar from "react-bootstrap/ButtonToolbar";
import BSCol from "react-bootstrap/Col";
import BSGrid from "react-bootstrap/Grid";
import BSLabel from "react-bootstrap/Label";
import BSPageHeader from "react-bootstrap/PageHeader";
import BSPager from "react-bootstrap/Pager";
import BSPanel from "react-bootstrap/Panel";
import BSRow from "react-bootstrap/Row";
import BSTable from "react-bootstrap/Table";
import {decodeEntities} from "security";
import FAIcon from "react-fa";
import Flag from "react-flags";
import {localizeDate} from "datetime";
import {noWhitespace} from "tempura";
import PropTypes from "prop-types";
import React, {Component} from "react";

import {makeMaximumBid, makeStandardBid, watch, unwatch} from "lot";
import {
  AE_AUCTION_BID_MODE,
  AE_LOT_STATUS,
} from "@auex/configurations";
import utilities from "@auex/utilities";

const {
  formats: {url},
} = utilities;

export class Header extends Component {
  static propTypes = {
    // Base URL for static assets.
    assetsUrl: PropTypes.string.isRequired,

    // The CC2 code for the country of the organization.
    auctionCountryCC2: PropTypes.string.isRequired,

    // The auction name.
    auctionName: PropTypes.string.isRequired,

    // The rank of the lot in the list.
    lotRank: PropTypes.number.isRequired,

    // The ID of the seller.
    orgFriendlyId: PropTypes.string.isRequired,

    // The seller's name output to the screen.
    orgName: PropTypes.string.isRequired,

    // The auction ID.
    shortAuctionId: PropTypes.string.isRequired,
  };

  static contextTypes = {
    __: PropTypes.func.isRequired,
  };

  render() {
    const {__} = this.context;
    const {
      assetsUrl,
      auctionCountryCC2,
      auctionName,
      lotRank,
      orgFriendlyId,
      orgName,
      shortAuctionId,
    } = this.props;

    return (
      <div>
        <span className="pull-right">
          {auctionCountryCC2 ? (
            <Flag
              basePath={`${assetsUrl}/img/flags`}
              country={auctionCountryCC2}
              pngSize={48}
              shiny
            />
          ) : null}
        </span>
        <BSPageHeader className="auctionHeader">
          <a href={noWhitespace `
              /${__("routes::seller")}
              /${orgFriendlyId}
              /${__("routes::catalog")}
              /${shortAuctionId}
              #lot${lotRank}
            `}
          >
            {`${decodeEntities(auctionName)} `}
          </a>
          <small>
            {`${__("general::by")} `}
            <a href={`/${__("routes::seller")}/${orgFriendlyId}`}>
              {decodeEntities(orgName)}
            </a>
          </small>
        </BSPageHeader>
      </div>
    );
  }
}

export class Body extends Component {
  static propTypes = {
    // Base URL for static assets.
    assetsUrl: PropTypes.string.isRequired,

    // The time the bidding period end.
    auctionBidEndDate: PropTypes.string.isRequired,

    // The number indicating the bid mode.
    auctionBidMode: PropTypes.number.isRequired,

    // The time the bidding period start.
    auctionBidStartDate: PropTypes.string.isRequired,

    // The CC2 code for the country of the organization.
    auctionCountryCC2: PropTypes.string.isRequired,

    // The organisation country.
    auctionCountryName: PropTypes.string.isRequired,

    // The current currency of the auction.
    auctionCurrency: PropTypes.shape({
      iso: PropTypes.shape({
        code: PropTypes.string.isRequired,
        number: PropTypes.string.isRequired,
      }).isRequired,
      name: PropTypes.string.isRequired,
      units: PropTypes.shape({
        major: PropTypes.shape({
          name: PropTypes.string.isRequired,
          symbol: PropTypes.string.isRequired,
        }).isRequired,
        minor: PropTypes.shape({
          majorValue: PropTypes.number.isRequired,
          name: PropTypes.string.isRequired,
          symbol: PropTypes.string.isRequired,
        }).isRequired,
      }).isRequired,
    }).isRequired,

    // The description associated with the auction.
    auctionDescription: PropTypes.string.isRequired,

    // The ID of the auction.
    auctionId: PropTypes.string.isRequired,

    // List of possible incrementation.
    auctionIncrements: PropTypes.array.isRequired,

    // Boolean indicating if auction is live or not.
    auctionLiveOngoing: PropTypes.bool,

    // The location of the auction.
    auctionLocation: PropTypes.shape({
      address: PropTypes.array.isRequired,
      city: PropTypes.string.isRequired,
      country: PropTypes.string.isRequired,
      postalCode: PropTypes.string.isRequired,
      region: PropTypes.string.isRequired,
    }),

    // The different payment methods available for the auction.
    auctionPaymentMethods: PropTypes.array.isRequired,

    auctionPremium: PropTypes.number.isRequired,

    auctionShipping: PropTypes.string.isRequired,

    // The start date of the auction.
    auctionStartDate: PropTypes.string.isRequired,

    // The status of the auction.
    auctionStatus: PropTypes.number.isRequired,

    // The taxes associated with this auction.
    auctionTaxes: PropTypes.array.isRequired,

    // The terms associated with this auction.
    auctionTerms: PropTypes.string.isRequired,

    // The auction type.
    auctionType: PropTypes.number.isRequired,

    // Prevent xss attack.
    csrfToken: PropTypes.string.isRequired,

    // The raw html auction item description.
    itemDescription: PropTypes.string.isRequired,

    // The different details of an item.
    itemDetails: PropTypes.array,

    // The item name.
    itemName: PropTypes.string.isRequired,

    // The different pictures associated with this item.
    itemPictures: PropTypes.array.isRequired,

    // The different bids made on this item.
    lotBids: PropTypes.array.isRequired,

    // The id of the lot.
    lotId: PropTypes.string.isRequired,

    // The details of the leading bid.
    lotLeadingBid: PropTypes.shape({
      amount: PropTypes.number.isRequired,
      date: PropTypes.string.isRequired,
      id: PropTypes.string.isRequired,
      user: PropTypes.string.isRequired,
    }),

    // The minimum amount a buyer has to make to bid on the lot item.
    lotMinimumBid: PropTypes.number.isRequired,

    // The rank of the lot in the list.
    lotRank: PropTypes.number.isRequired,

    // The opening bid on an item.
    lotStartingBid: PropTypes.number.isRequired,

    // The lot status.
    lotStatus: PropTypes.number.isRequired,

    // The ID of the next lot item.
    nextLot: PropTypes.string.isRequired,

    // The ID of the seller.
    orgFriendlyId: PropTypes.string.isRequired,

    // The live auction url.
    orgUrl: PropTypes.string.isRequired,

    // Bool indicating when this user has been overbidded by another user.
    overbidded: PropTypes.bool.isRequired,

    // The ID of the previous lot item.
    previousLot: PropTypes.string.isRequired,

    // The auction ID.
    shortAuctionId: PropTypes.string.isRequired,

    // Whether the current lot is under approval.
    underApproval: PropTypes.bool,

    // The current user ID.
    userId: PropTypes.string,

    // The auth user last bid.
    userLastBid: PropTypes.number.isRequired,

    userMaximumBid: PropTypes.number.isRequired,

    // Indicating if the user currently watch this lot.
    userWatchesLot: PropTypes.bool.isRequired,
  };

  static defaultProps = {
    auctionLiveOngoing: false,
  };

  static contextTypes = {
    __: PropTypes.func.isRequired,
  };

  render() {
    const {__} = this.context;
    const {
      assetsUrl,
      auctionBidEndDate,
      auctionBidMode,
      auctionBidStartDate,
      auctionCountryCC2,
      auctionCountryName,
      auctionCurrency,
      auctionDescription,
      auctionId,
      auctionIncrements,
      auctionLiveOngoing,
      auctionLocation,
      auctionPaymentMethods,
      auctionPremium,
      auctionShipping,
      auctionStartDate,
      auctionStatus,
      auctionTaxes,
      auctionTerms,
      auctionType,
      csrfToken,
      itemDescription,
      itemDetails,
      itemName,
      itemPictures,
      loggedin,
      lotBids,
      lotId,
      lotLeadingBid,
      lotMinimumBid,
      lotName,
      lotRank,
      lotStartingBid,
      lotStatus,
      nextLot,
      orgFriendlyId,
      orgUrl,
      overbidded,
      previousLot,
      shortAuctionId,
      underApproval,
      userId,
      userLastBid,
      userMaximumBid,
      userWatchesLot,
    } = this.props;

    const localizedAuctionDate = localizeDate({date: auctionStartDate});

    return (
      <div>
        <div className="auctionHeader">
          <BSGrid fluid>
            <BSRow>
              <BSCol md={6}>
                <h3>
                  {`${__("auction::catalog.subheaderInformation")} `}
                  {localizedAuctionDate.date}
                  <small>{` ${localizedAuctionDate.timezone}`}</small>
                </h3>
              </BSCol>
              <BSCol className="text-right" md={6}>
                <BSRow>
                  <BSButtonToolbar className="pull-right">
                    {auctionLiveOngoing ? (
                      <BSButton
                        bsSize="xs"
                        bsStyle="success"
                        href={url(orgUrl, {auctionId, orgFriendlyId})}
                      >
                        <strong>
                          <FAIcon name="play" />
                          {` ${__("auction::catalog.joinLive")}`}
                        </strong>
                      </BSButton>
                    ) : null}
                    <AEWatchButton
                      actions={{
                        lot: {
                          unwatch,
                          watch: bindIfLoggedIn(csrfToken, watch),
                        },
                      }}
                      loggedin={loggedin}
                      lotId={lotId}
                      type="lot"
                      watched={userWatchesLot || false}
                    />
                  </BSButtonToolbar>
                </BSRow>
                {/* FIXME: Reintroduce when the flow behind it is supported. See AUEX-459
                  <a type="button" class="btn btn-success btn-xs navbar-right navbar-space"><i class="fa fa-hand-o-up"></i> {{ __('auction::catalog.registerForThisAuction') }}</a>
                */}
              </BSCol>
            </BSRow>
          </BSGrid>
        </div>
        <div id="lotPage">
          <BSPager>
            {previousLot ? (
              <BSPager.Item
                href={noWhitespace`
                  /${__("routes::seller")}
                  /${orgFriendlyId}
                  /${__("routes::auction")}
                  /${shortAuctionId}
                  /${__("routes::lot")}
                  /${previousLot}
                `}
                previous
              >
                &larr; {__("auction::lot.previousLot")}
              </BSPager.Item>
            ) : null}

            {nextLot ? (
              <BSPager.Item
                href={noWhitespace`
                  /${__("routes::seller")}
                  /${orgFriendlyId}
                  /${__("routes::auction")}
                  /${shortAuctionId}
                  /${__("routes::lot")}
                  /${nextLot}
                `}
                next
              >
                {__("auction::lot.nextLot")} &rarr;
              </BSPager.Item>
            ) : null}
          </BSPager>
          <BSRow>
            <BSCol md={9}>
              <h3>
                {`${__("terminology::terms.lot.term")} # ${lotRank}`}
                {` - ${decodeEntities(itemName)} `}
                {lotStatus !== AE_LOT_STATUS.OPEN ? (
                  <AELotStatusLabel
                    status={lotStatus}
                  />
                ) : null}
                {underApproval ? (
                  <small className="text-warning">
                    {__("lot::underApproval.name").toUpperCase()}
                  </small>
                ) : null}
              </h3>
            </BSCol>
            <BSCol md={3}>
              <div className="pull-right">
                <AEShareBar
                  shared={__("lot::shareThis")}
                  title={lotName}
                />
              </div>
            </BSCol>
          </BSRow>
          <BSRow>
            <BSCol md={5}>
              <AELotPicture
                pictures={itemPictures.map(
                  ({thumbnails}) => thumbnails,
                )}
              />

              <BSPanel header={<h3>{__("auction::catalog.description")}</h3>}>
                {itemDescription ? (
                  <div dangerouslySetInnerHTML={{__html: itemDescription}} />
                ) : (
                  <p>{__("auction::catalog.noDescription")}.</p>
                )}
              </BSPanel>

              <BSPanel
                header={<h3>{__("auction::lot.paymentAndPremiums")}</h3>}
              >
                <h6>
                  {`${__("organization::buyerPremium")} `}
                  <BSLabel
                    title={__("terminology::terms.buyerPremium.definition")}
                  >
                    ?
                  </BSLabel>
                </h6>
                <p>{auctionPremium}%</p>
                <AEAcceptedPaymentMethods
                  paymentMethods={auctionPaymentMethods}
                />
                <h6>{__("auction::lot.currency")}</h6>
                <p>
                  {`${auctionCurrency.name} `}
                  {auctionCurrency.units.major.symbol || null}
                </p>
              </BSPanel>

              <BSPanel
                header={<h3>{__("auction::lot.incrementsTable")}</h3>}
              >
                <AEBidIncrementsEditor
                  currencyType={auctionCurrency.iso.code}
                  highlightCurrentBid={lotLeadingBid && lotLeadingBid.amount}
                  increments={auctionIncrements}
                  readOnly
                />
              </BSPanel>
            </BSCol>

            <BSCol md={7}>
              {auctionBidMode !== AE_AUCTION_BID_MODE.NONE ? (
                <AEAbsenteeBidModule
                  actions={{
                    lot: {
                      makeMaximumBid,
                      makeStandardBid,
                    },
                  }}
                  auction={{
                    bidEndDate: auctionBidEndDate,
                    bidMode: auctionBidMode,
                    bidStartDate: auctionBidStartDate,
                    currency: auctionCurrency,
                    id: auctionId,
                    increments: auctionIncrements,
                    startDate: auctionStartDate,
                    status: auctionStatus,
                    type: auctionType,
                  }}
                  lot={{
                    bids: lotBids,
                    id: lotId,
                    leadingBid: lotLeadingBid,
                    minimumBid: lotMinimumBid,
                    startingBid: lotStartingBid,
                    status: lotStatus,
                  }}
                  user={{
                    id: userId,
                    lastBid: userLastBid,
                    maximumBid: userMaximumBid,
                    overbidded,
                  }}
                />
              ) : null}
              {itemDetails && (itemDetails.length > 0) ? (
                <BSPanel
                  className="offer"
                  header={<h3>{__("auction::lot.lotDetails")}</h3>}
                >
                  <BSTable striped>
                    <thead>
                      <tr>
                        <th>{__("auction::lot.key")}</th>
                        <th>{__("auction::lot.value")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {itemDetails.map(
                        (itemDetail, index) => (
                          <tr key={index}>
                            <td>{itemDetail.key}</td>
                            <td>{itemDetail.value}</td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </BSTable>
                </BSPanel>
              ) : null}
              {/* Nav tabs & Panels */}
              <BSPanel
                className="auctionInformation"
                header={<h3>{__("auction::lot.auctionInformation")}</h3>}
              >
                <AuctionInformationTabs
                  assetsUrl={assetsUrl}
                  auctionLocation={auctionLocation}
                  countryCC2={auctionCountryCC2}
                  countryName={auctionCountryName}
                  description={auctionDescription}
                  shipping={auctionShipping}
                  taxes={auctionTaxes}
                  terms={auctionTerms}
                />
              </BSPanel>
            </BSCol>
          </BSRow>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./lot.jsx